import { Alert, Button, Card, CardBody, CardHeader, Field, Input } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  cancelDeletion,
  deletionQuery,
  requestDeletion,
} from '../../shared/api/deletion-queries.js';

/**
 * Deleting the business.
 *
 * Built around the mistake it must not make. The owner types the business's
 * name and their own password, nothing happens for thirty days, everyone in the business sees a
 * banner the whole time, and one click cancels it. After the thirty days the
 * books, the receipts and every file are gone, and that cannot be undone, so
 * the card says to export first, in those words.
 */
export function DeleteBusinessCard({
  organizationId,
  organizationName,
  isOwner,
}: {
  organizationId: string;
  organizationName: string;
  isOwner: boolean;
}) {
  const queryClient = useQueryClient();
  const pending = useQuery(deletionQuery(organizationId));
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['deletion', organizationId] });

  const request = useMutation({
    mutationFn: () => requestDeletion(organizationId, confirmName, password, reason),
    onSuccess: () => {
      setConfirmName('');
      setReason('');
      setPassword('');
      setError(null);
      refresh();
    },
    onError: (caught) => {
      // Never leave a password sitting in a field after it was refused.
      setPassword('');
      setError(caught instanceof Error ? caught.message : 'The request was not made.');
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelDeletion(organizationId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The deletion was not cancelled.'),
  });

  const scheduled = pending.data ?? null;

  return (
    <Card>
      <CardHeader
        title="Delete this business"
        description="Removes the books, every receipt and file, and every member's access. It happens thirty days after it is asked for, and it cannot be undone."
      />
      <CardBody className="flex flex-col gap-3">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}

        {scheduled !== null ? (
          <>
            <Alert tone="warning" title="This business is scheduled for deletion">
              Everything will be deleted on{' '}
              {new Date(scheduled.purgeAfter).toLocaleDateString('en-CA')}
              {scheduled.requestedByName === null ? '' : `, as ${scheduled.requestedByName} asked`}.
              Until then nothing changes, and it can be cancelled.
            </Alert>
            {isOwner ? (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => cancel.mutate()}
                  isLoading={cancel.isPending}
                >
                  Cancel the deletion
                </Button>
              </div>
            ) : null}
          </>
        ) : !isOwner ? (
          <p className="text-sm text-content-muted">Only the owner can delete the business.</p>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              request.mutate();
            }}
          >
            <p className="text-sm text-content">
              The CRA expects a business to keep its records for six years. Export everything first;
              after deletion there is no copy to recover.
            </p>
            <Field label={`Type ${organizationName} to confirm`}>
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={confirmName}
                  autoComplete="off"
                  onChange={(event) => setConfirmName(event.target.value)}
                />
              )}
            </Field>
            <Field label="Your password">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>
            <Field label="Why (optional)">
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Field>
            <div>
              <Button
                type="submit"
                variant="danger"
                isLoading={request.isPending}
                disabled={confirmName.trim() !== organizationName.trim() || password === ''}
              >
                Delete in thirty days
              </Button>
            </div>
          </form>
        )}
      </CardBody>
    </Card>
  );
}
